import { db } from '../db/indexeddb';
import { shouldMakeAPICall } from '../utils/network';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/api/resources';

/**
 * Master Data Repository
 * Handles cost centers, GL codes, project codes, items, departments, vendors, users, PR codes
 */
export class MasterDataRepository {
  /**
   * Cost Centers
   */
  async getCostCentersFromCache() {
    try {
      const centers = await db.costCenters.toArray();
      return centers.map(c => c.data);
    } catch (error) {
      console.error('Error reading cost centers from cache:', error);
      return [];
    }
  }

  async saveCostCentersToCache(centers) {
    try {
      await db.transaction('rw', db.costCenters, async () => {
        await db.costCenters.clear();
        const data = centers.map(c => ({
          costcenter_code: c.costcenter_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.costCenters.bulkAdd(data);
      });
      console.log(`✅ Cached ${centers.length} cost centers`);
    } catch (error) {
      console.error('Error saving cost centers to cache:', error);
    }
  }

  async syncCostCenters(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getCostCentersFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.cost_center_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.data || [];
      await this.saveCostCentersToCache(data);
      return data;
    } catch (error) {
      console.error('Error syncing cost centers:', error);
      return this.getCostCentersFromCache();
    }
  }

  /**
   * GL Codes
   */
  async getGLCodesFromCache() {
    try {
      const codes = await db.glCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async saveGLCodesToCache(codes) {
    try {
      await db.transaction('rw', db.glCodes, async () => {
        await db.glCodes.clear();
        const data = codes.map(c => ({
          gl_code: c.gl_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.glCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} GL codes`);
    } catch (error) {
      console.error('Error saving GL codes to cache:', error);
    }
  }

  async syncGLCodes(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getGLCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.gl_code_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.data || [];
      await this.saveGLCodesToCache(data);
      return data;
    } catch (error) {
      return this.getGLCodesFromCache();
    }
  }

  /**
   * Project Codes
   */
  async getProjectCodesFromCache() {
    try {
      const codes = await db.projectCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async saveProjectCodesToCache(codes) {
    try {
      await db.transaction('rw', db.projectCodes, async () => {
        await db.projectCodes.clear();
        const data = codes.map(c => ({
          project_code: c.project_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.projectCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} project codes`);
    } catch (error) {
      console.error('Error saving project codes to cache:', error);
    }
  }

  async syncProjectCodes(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getProjectCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.projects_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.data || [];
      await this.saveProjectCodesToCache(data);
      return data;
    } catch (error) {
      return this.getProjectCodesFromCache();
    }
  }

  /**
   * Items
   */
  async getItemsFromCache() {
    try {
      const items = await db.items.toArray();
      return items.map(i => i.data);
    } catch (error) {
      return [];
    }
  }

  async saveItemsToCache(items) {
    try {
      await db.transaction('rw', db.items, async () => {
        await db.items.clear();
        const data = items.map(item => ({
          item_code: item.item_code || '',
          item_token: item.item_token || '',
          data: item,
          lastSynced: Date.now(),
        }));
        await db.items.bulkAdd(data);
      });
      console.log(`✅ Cached ${items.length} items`);
    } catch (error) {
      console.error('Error saving items to cache:', error);
    }
  }

  async syncItems(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getItemsFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.view_all_item,
        { org_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data?.response || [];
      await this.saveItemsToCache(data);
      return data;
    } catch (error) {
      return this.getItemsFromCache();
    }
  }

  /**
   * Departments
   */
  async getDepartmentsFromCache() {
    try {
      const depts = await db.departments.toArray();
      return depts.map(d => d.data);
    } catch (error) {
      return [];
    }
  }

  async saveDepartmentsToCache(departments) {
    try {
      await db.transaction('rw', db.departments, async () => {
        await db.departments.clear();
        const data = departments.map(d => ({
          name: d.name || '',
          data: d,
          lastSynced: Date.now(),
        }));
        await db.departments.bulkAdd(data);
      });
      console.log(`✅ Cached ${departments.length} departments`);
    } catch (error) {
      console.error('Error saving departments to cache:', error);
    }
  }

  async syncDepartments(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getDepartmentsFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.get_Department,
        { user_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data?.data || [];
      await this.saveDepartmentsToCache(data);
      return data;
    } catch (error) {
      return this.getDepartmentsFromCache();
    }
  }

  /**
   * Vendors
   */
  async getVendorsFromCache() {
    try {
      const vendors = await db.vendors.toArray();
      return vendors.map(v => v.data);
    } catch (error) {
      return [];
    }
  }

  async saveVendorsToCache(vendors) {
    try {
      await db.transaction('rw', db.vendors, async () => {
        await db.vendors.clear();
        const data = vendors.map(v => ({
          token: v.token || '',
          data: v,
          lastSynced: Date.now(),
        }));
        await db.vendors.bulkAdd(data);
      });
      console.log(`✅ Cached ${vendors.length} vendors`);
    } catch (error) {
      console.error('Error saving vendors to cache:', error);
    }
  }

  async syncVendors(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getVendorsFromCache();
    }

    try {
      const response = await axios({
        method: 'POST',
        url: API_ENDPOINT.get_all_vendor,
        data: { org_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.users || [];
      await this.saveVendorsToCache(data);
      return data;
    } catch (error) {
      return this.getVendorsFromCache();
    }
  }

  /**
   * Users
   */
  async getUsersFromCache() {
    try {
      const users = await db.users.toArray();
      return users.map(u => u.data);
    } catch (error) {
      return [];
    }
  }

  async saveUsersToCache(users) {
    try {
      await db.transaction('rw', db.users, async () => {
        await db.users.clear();
        const data = users.map(u => ({
          token: u.token || '',
          data: u,
          lastSynced: Date.now(),
        }));
        await db.users.bulkAdd(data);
      });
      console.log(`✅ Cached ${users.length} users`);
    } catch (error) {
      console.error('Error saving users to cache:', error);
    }
  }

  async syncUsers(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getUsersFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.all_user_list,
        { buyer_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data || [];
      await this.saveUsersToCache(data);
      return data;
    } catch (error) {
      return this.getUsersFromCache();
    }
  }

  /**
   * PR Codes
   */
  async getPRCodesFromCache() {
    try {
      const codes = await db.prCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async savePRCodesToCache(codes) {
    try {
      await db.transaction('rw', db.prCodes, async () => {
        await db.prCodes.clear();
        const data = codes.map(c => ({
          pr_code: c.pr_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.prCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} PR codes`);
    } catch (error) {
      console.error('Error saving PR codes to cache:', error);
    }
  }

  async syncPRCodes(authToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getPRCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.get_all_saved_prs_code,
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data || [];
      await this.savePRCodesToCache(data);
      return data;
    } catch (error) {
      return this.getPRCodesFromCache();
    }
  }
}

export const masterDataRepository = new MasterDataRepository();
